import { useCallback, useState } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Button, IconButton } from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import clsx from 'clsx';
import {
  ConditionalTemplateQuestion_FieldsFragment,
  ConditionalTemplateQuestion_GetConditionalTemplateQuestionsByConditionalTemplateQuestionSetIdDocument,
  useConditionalTemplateQuestion_DeleteConditionalTemplateQuestionByIdMutation,
} from 'queries';
import { Icon } from 'ts-react-feather-icons';

import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import { useStyles } from './conditional-template-question-actions.styles';

type Props = {
  conditionalTemplateQuestion: ConditionalTemplateQuestion_FieldsFragment;
  onEditSelectedConditionalTemplateQuestion: Function;
};

export const ConditionalTemplateQuestionActions = ({
  conditionalTemplateQuestion,
  onEditSelectedConditionalTemplateQuestion,
}: Props) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [deleteConditionalTemplateQuestion] =
    useConditionalTemplateQuestion_DeleteConditionalTemplateQuestionByIdMutation({
      refetchQueries: [
        {
          query:
            ConditionalTemplateQuestion_GetConditionalTemplateQuestionsByConditionalTemplateQuestionSetIdDocument,
          variables: {
            input: {
              conditionalTemplateQuestionSetId:
                conditionalTemplateQuestion.conditionalTemplateQuestionSetId,
            },
          },
        },
      ],
      onCompleted: () => {
        showToast({
          message: 'Selected question has been deleted',
          severity: NotificationSeverity.SUCCESS,
        });
      },
      onError: (e) => {
        showAlert({
          message: 'Error deleting selected question: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const editConditionalTemplateQuestion = () => {
    onEditSelectedConditionalTemplateQuestion();
  };

  const handleDeleteConditionalTemplateQuestion = () => {
    if (
      !window.confirm(
        'Are you sure you want to delete this question and all associated answers and filers?',
      )
    ) {
      return;
    }
    deleteConditionalTemplateQuestion({
      variables: {
        id: conditionalTemplateQuestion.id,
      },
    });
  };

  const getMenuItems = useCallback(() => {
    const menuItems = [
      {
        key: 'actions',
        label: 'Edit',
        icon: <Icon name="edit-2" size={16} color="#44D2F2" />,
        onClick: () => {
          editConditionalTemplateQuestion();
        },
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <DeleteIcon
            className={clsx(classes.actionButton, classes.redButtonColor)}
            fontSize="small"
          />
        ),
        onClick: () => {
          handleDeleteConditionalTemplateQuestion();
        },
      },
    ];

    return menuItems;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionalTemplateQuestion]);

  return (
    <>
      <IconButton
        id="action-button"
        aria-controls={open ? 'action-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
      >
        <MoreHorizIcon />
      </IconButton>
      <Menu
        PaperProps={{
          elevation: 0,
          className: classes.menu,
        }}
        id="action-menu"
        MenuListProps={{
          'aria-labelledby': 'action-button',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {getMenuItems().map((item) => (
          <MenuItem key={item.key} disableRipple className={classes.menuItem}>
            <Button
              className={classes.conditionalTemplateQuestionActionButton}
              size="small"
              startIcon={item.icon}
              onClick={item.onClick}
            >
              {item.label}
            </Button>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
